import legacyClasses from './legacy-classes.json';

const legacy: Record<string, string> = legacyClasses;
const tones = { primary: 'primary', secondary: 'secondary', success: 'success', warning: 'warning', alert: 'error' } as const;

/** Shared V1 renderers and legacy AJAX fragments still emit old class names. No legacy CSS is loaded. */
export function enhanceAppearance(root: ParentNode) {
    const nodes = [...(root instanceof HTMLElement ? [root] : []), ...root.querySelectorAll<HTMLElement>('*')];
    for (const node of nodes) {
        if (node.closest('.cke, .datepicker')) continue;
        const classes = new Set([...node.classList].flatMap(name => (legacy[name] || name).split(' ')));
        if ([...classes].some(name => /^w-\[calc\(\d+\/12/.test(name))) classes.delete('w-[calc(100%_-_var(--gutter,0px))]');
        if (classes.has('switch-paddle')) classes.delete('sr-only');
        if (classes.has('switch-active') || classes.has('switch-inactive')) node.removeAttribute('aria-hidden');
        const component = classes.has('button') ? 'btn' : classes.has('callout') ? 'alert' : classes.has('label') ? 'badge' : null;
        if (component) {
            const tone = Object.entries(tones).find(([name]) => name !== 'primary' && classes.has(name))?.[1] || 'primary';
            for (const value of Object.values(tones)) classes.delete(`dui-${component}-${value}`);
            if (component !== 'alert' || Object.keys(tones).some(key => classes.has(key))) classes.add(`dui-${component}-${tone}`);
            if (component === 'btn') {
                for (const size of ['xs', 'sm', 'lg']) classes.delete(`dui-btn-${size}`);
                const size = classes.has('tiny') ? 'xs' : classes.has('small') ? 'sm' : classes.has('large') ? 'lg' : null;
                if (size) classes.add(`dui-btn-${size}`);
                for (const [old, modern] of Object.entries({ hollow: 'dui-btn-outline', clear: 'dui-btn-ghost', expanded: 'w-full', disabled: 'dui-btn-disabled' })) {
                    if (classes.has(old)) classes.add(modern); else classes.delete(modern);
                }
            } else if (component === 'alert') {
                const tinted = Object.keys(tones).some(key => classes.has(key));
                if (tinted) classes.delete('bg-base-100'); else classes.add('bg-base-100');
                classes.add('dui-alert-soft');
                if (classes.has('small')) classes.add('p-2!');
            }
        }
        if (node instanceof HTMLInputElement) {
            const type = node.type;
            if (type === 'checkbox' || type === 'radio') {
                if (!classes.has('switch-input')) { classes.add(`dui-${type}`); classes.add(`dui-${type}-sm`); classes.add(`dui-${type}-primary`); }
            } else if (type === 'file') classes.add('w-full');
            else if (!['hidden', 'submit', 'button', 'image', 'range'].includes(type)) {
                if (node.parentElement?.closest('.dui-input')) classes.delete('dui-input');
                else classes.add('dui-input');
            }
        }
        if (node instanceof HTMLSelectElement && !node.multiple && !classes.has('select-multi')) classes.add('dui-select');
        if (node instanceof HTMLTextAreaElement) classes.add('dui-textarea');
        if (node instanceof HTMLTableElement) {
            classes.add('dui-table');
            if (!classes.has('unstriped')) classes.add('dui-table-zebra');
        }
        if (node.matches('.tabs-title > a')) classes.add('dui-tab');
        if (node.matches('.switch-input')) {
            const title = node.nextElementSibling?.querySelector('.show-for-sr, .sr-only')?.textContent?.trim();
            if (title && !node.hasAttribute('aria-label')) node.setAttribute('aria-label', title);
        }
        const next = [...classes].join(' ');
        if (next !== node.className) node.className = next;
    }
}
